import axios from 'axios';

// API alap URL
export const API_URL = process.env.REACT_APP_API_URL;

// Axios instance létrehozása
const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Token interceptor
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Project service
export const projectService = {
    // Összes projekt lekérése
    getAllProjects: async () => {
        try {
            const response = await api.get('/projects');
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt a projektek lekérése során.' };
        }
    },

    // Kiemelt projektek lekérése
    getFeaturedProjects: async () => {
        try {
            const response = await api.get('/projects/featured');
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt a kiemelt projektek lekérése során.' };
        }
    },

    // Egy projekt lekérése ID alapján
    getProjectById: async (id) => {
        try {
            const response = await api.get(`/projects/${id}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt a projekt lekérése során.' };
        }
    },

    // Új projekt létrehozása
    createProject: async (projectData) => {
        try {
            // Ha FormData-t kaptunk, közvetlenül küldjük el
            if (projectData instanceof FormData) {
                const response = await api({
                    method: 'post',
                    url: '/projects',
                    data: projectData,
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                return response.data;
            }

            // Ha nem FormData, akkor a régi logika szerint
            const { images, ...data } = projectData;
            const requestData = {
                ...data,
                start_date: data.startDate,
                end_date: data.endDate,
                workers: Array.isArray(data.workers) ? data.workers : []
            };

            const response = await api.post('/projects', requestData);
            return response.data;
        } catch (error) {
            console.error('API Hiba:', error);
            throw error.response?.data || { message: 'Hiba történt a projekt létrehozása során.' };
        }
    },

    // Projekt frissítése
    updateProject: async (id, projectData) => {
        try {
            // Ha FormData-t kaptunk, közvetlenül küldjük el
            if (projectData instanceof FormData) {
                const response = await api({
                    method: 'put',
                    url: `/projects/${id}`,
                    data: projectData,
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                return response.data;
            }

            // Ha nem FormData, akkor a régi logika szerint
            const { images, ...data } = projectData;
            const requestData = {
                ...data,
                start_date: data.startDate,
                end_date: data.endDate,
                workers: Array.isArray(data.workers) ? data.workers : []
            };

            const response = await api.put(`/projects/${id}`, requestData);
            return response.data;
        } catch (error) {
            console.error('API Hiba:', error);
            throw error.response?.data || { message: 'Hiba történt a projekt frissítése során.' };
        }
    },

    // Projekt törlése
    deleteProject: async (id) => {
        try {
            const response = await api.delete(`/projects/${id}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt a projekt törlése során.' };
        }
    },

    // Projekt kép törlése
    deleteProjectImage: async (projectId, imageId) => {
        try {
            const response = await api.delete(`/projects/${projectId}/images/${imageId}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt a kép törlése során.' };
        }
    }
};

// Auth service
export const authService = {
    // Bejelentkezés
    login: async (credentials) => {
        try {
            const response = await api.post('/auth/login', credentials);
            if (response.data.token) {
                localStorage.setItem('token', response.data.token);
                localStorage.setItem('user', JSON.stringify(response.data.user));
            }
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt a bejelentkezés során.' };
        }
    },

    // Kijelentkezés
    logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    },

    // Token ellenőrzése
    verifyToken: async () => {
        try {
            const response = await api.get('/auth/verify');
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt a token ellenőrzése során.' };
        }
    },

    // Jelszó módosítása
    changePassword: async (data) => {
        try {
            const response = await api.post('/auth/change-password', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt a jelszó módosítása során.' };
        }
    }
};

// User service
export const userService = {
    // Összes felhasználó lekérése
    getAllUsers: async () => {
        try {
            const response = await api.get('/users');
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt a felhasználók lekérése során.' };
        }
    },

    // Egy felhasználó lekérése ID alapján
    getUserById: async (id) => {
        try {
            const response = await api.get(`/users/${id}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt a felhasználó lekérése során.' };
        }
    },

    // Új felhasználó létrehozása
    createUser: async (userData) => {
        try {
            const response = await api.post('/users', userData);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt a felhasználó létrehozása során.' };
        }
    },

    // Felhasználó frissítése
    updateUser: async (id, userData) => {
        try {
            const response = await api.put(`/users/${id}`, userData);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt a felhasználó frissítése során.' };
        }
    },

    // Felhasználó törlése
    deleteUser: async (id) => {
        try {
            const response = await api.delete(`/users/${id}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt a felhasználó törlése során.' };
        }
    }
};

// Calendar service
export const calendarService = {
    // Felhasználó eseményeinek lekérése
    getUserEvents: async (userId) => {
        try {
            const response = await api.get(`/calendar/user/${userId}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt az események lekérése során.' };
        }
    },

    // Felhasználó havi eseményeinek lekérése
    getUserMonthEvents: async (userId, year, month) => {
        try {
            const response = await api.get(`/calendar/user/${userId}/month/${year}/${month}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt az események lekérése során.' };
        }
    },

    // Új esemény létrehozása
    createEvent: async (eventData) => {
        try {
            const response = await api.post('/calendar', eventData);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt az esemény létrehozása során.' };
        }
    },

    // Esemény frissítése
    updateEvent: async (id, eventData) => {
        try {
            const response = await api.put(`/calendar/${id}`, eventData);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt az esemény frissítése során.' };
        }
    },

    // Esemény törlése
    deleteEvent: async (id) => {
        try {
            const response = await api.delete(`/calendar/${id}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt az esemény törlése során.' };
        }
    }
};

// Quote service
export const quoteService = {
    // Összes árajánlat lekérése
    getAllQuotes: async () => {
        try {
            const response = await api.get('/quotes');
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt az árajánlatok lekérése során.' };
        }
    },

    // Egy árajánlat lekérése ID alapján
    getQuoteById: async (id) => {
        try {
            const response = await api.get(`/quotes/${id}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt az árajánlat lekérése során.' };
        }
    },

    // Új árajánlat létrehozása
    createQuote: async (quoteData) => {
        try {
            const response = await api.post('/quotes', quoteData);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt az árajánlat létrehozása során.' };
        }
    },

    // Árajánlat frissítése
    updateQuote: async (id, quoteData) => {
        try {
            const response = await api.put(`/quotes/${id}`, quoteData);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt az árajánlat frissítése során.' };
        }
    },

    // Árajánlat törlése
    deleteQuote: async (id) => {
        try {
            const response = await api.delete(`/quotes/${id}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt az árajánlat törlése során.' };
        }
    },

    // Árajánlat küldése emailben
    sendQuote: async (id) => {
        try {
            const response = await api.post(`/quotes/${id}/send`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Hiba történt az árajánlat küldése során.' };
        }
    }
};
